<template>
  <div class="applicants-container page-container">
    <div class="header-container">
      <div class="title-section">
        <h1 class="display-heading">
          <i class="fas fa-users"></i>
          Bewerber
        </h1>
      </div>
      <div class="search-section">
        <div class="search-input-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            class="form-control search-input-enhanced"
            v-model="searchQueryInput" 
            placeholder="Nach Name oder E-Mail suchen..."
            @input="debounceSearch" 
          >
          <button 
            v-if="searchQueryInput"
            @click="clearSearch" 
            class="clear-search-btn" 
            aria-label="Suche löschen"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <Card class="card-with-table">
      <div class="applicants-container">
        <div v-if="loading" class="loading-container">
          <div class="loader"></div>
          <p>Bewerber werden geladen...</p>
        </div>

        <div v-else-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          <p>{{ error }}</p>
          <button class="btn btn-secondary" @click="loadApplicants">Erneut versuchen</button>
        </div>

        <div v-else-if="filteredApplicants.length === 0" class="empty-state">
          <i class="fas fa-user-plus empty-icon"></i>
          <h3>Keine Bewerber gefunden</h3>
          <p v-if="filters.search">Keine Ergebnisse für "{{ filters.search }}"</p>
          <p v-else>Es wurden noch keine Bewerber angelegt. Erstellen Sie Ihren ersten Bewerber, um zu beginnen.</p>
        </div>

        <div v-else>
          <table class="applicants-table">
            <thead>
              <tr>
                <th @click="sortBy('name')" class="sortable">
                  Name
                  <i v-if="sortKey === 'name'" :class="sortIconClass"></i>
                </th>
                <th @click="sortBy('email')" class="sortable">
                  E-Mail
                  <i v-if="sortKey === 'email'" :class="sortIconClass"></i>
                </th>
                <th>Telefon</th>
                <th @click="sortBy('created_at')" class="sortable">
                  Erstellt am
                  <i v-if="sortKey === 'created_at'" :class="sortIconClass"></i>
                </th>
                <th>Tests</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr @click="viewApplicant(applicant)" style="cursor: pointer;" v-for="applicant in filteredApplicants" :key="applicant.id" class="applicant-row">
                <td class="name-cell">
                  <div class="applicant-name">
                    <div class="avatar">
                      {{ getInitials(applicant.first_name, applicant.last_name) }}
                    </div>
                    <div class="name-details">
                      <span class="full-name">{{ applicant.first_name }} {{ applicant.last_name }}</span>
                    </div>
                  </div>
                </td>
                <td>{{ applicant.email }}</td>
                <td>{{ applicant.phone || '-' }}</td>
                <td>{{ formatDate(applicant.created_at) }}</td>
                <td>{{ applicant.entries.length || 0 }}</td>
                <td>
                  <span class="status-badge" :class="getStatusClass(applicant.status)">
                    {{ getStatusLabel(applicant.status) }}
                  </span>
                </td>
                <td class="actions-cell">
                  <button class="btn-icon" @click="viewApplicant(applicant)" title="Details anzeigen">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="btn-icon" @click="deleteApplicant(applicant)" title="Löschen">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date';
import { useApplicantsStore } from '@/stores/applicants';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { API_BASE_URL } from '@/config/api';
import Card from '@/components/ui/Card.vue';

export default {
  name: 'ApplicantsList',
  components: {
    Card
  },
  data() {
    return {
      loading: false,
      error: null,
      applicantsStore: null,
      authStore: null,
      companyStore: null,
      localApplicants: [], // Lokale Kopie der Bewerber-Daten als sicheres Fallback
      apiUrl: API_BASE_URL,
      isDevelopment: process.env.NODE_ENV === 'development',
      filters: {
        search: '',
        sort: 'created_at',
        direction: 'desc'
      },
      debounceTimeout: null, // For debounce logic
      searchQueryInput: '' // Temporary input model
    };
  },
  computed: {
    // Authentifizierungsinformationen
    isAuthenticated() {
      return this.authStore?.isAuthenticated || false;
    },
    user() {
      return this.authStore?.user || null;
    },
    company() {
      return this.companyStore?.company || null;
    },
    // Bewerber-Daten
    applicants() {
      return Array.isArray(this.applicantsStore?.applicants) 
        ? this.applicantsStore.applicants 
        : this.localApplicants;
    },
    filteredApplicants() {
      const applicantsArray = Array.isArray(this.applicants) ? this.applicants : [];
      if (applicantsArray.length === 0) return [];
      let result = [...applicantsArray];

      // Filter using filters.search (updated by debounceSearch)
      if (this.filters.search) {
        const query = this.filters.search.toLowerCase();
        result = result.filter(applicant => {
          return (
            (applicant.first_name || '').toLowerCase().includes(query) ||
            (applicant.last_name || '').toLowerCase().includes(query) ||
            (applicant.email || '').toLowerCase().includes(query)
          );
        });
      }
      
      // Sortieren
      result.sort((a, b) => {
        let aValue = a[this.filters.sort];
        let bValue = b[this.filters.sort];
        
        if (aValue < bValue) return this.filters.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.filters.direction === 'asc' ? 1 : -1;
        return 0;
      });
      
      return result;
    },
    sortIconClass() {
      return this.filters.direction === 'asc' 
        ? 'fas fa-sort-up' 
        : 'fas fa-sort-down';
    }
  },
  created() {
    this.initializeStore();
  },
  methods: {
    initializeStore() {
      try {
        // Initialisiere Pinia-Stores
        this.authStore = useAuthStore();
        this.companyStore = useCompanyStore();
        this.applicantsStore = useApplicantsStore();
        
        console.log('Pinia-Stores initialisiert:', {
          auth: this.authStore,
          company: this.companyStore,
          applicants: this.applicantsStore
        });
        
        // Prüfen, ob der Benutzer angemeldet ist
        if (!this.authStore.isAuthenticated) {
          console.warn('Benutzer ist nicht angemeldet!');
          this.error = 'Sie sind nicht angemeldet. Bitte melden Sie sich an, um Bewerber zu sehen.';
          this.loading = false;
          
          // Zur Login-Seite weiterleiten
          setTimeout(() => {
            this.$router.push('/login');
          }, 2000);
          return;
        }
        
        // Store initialisieren und Bewerber laden
        this.loadApplicants();
      } catch (error) {
        console.error('Fehler bei der Initialisierung der Pinia-Stores:', error);
        this.error = 'Fehler bei der Initialisierung der Anwendung';
        this.loading = false;
      }
    },
    formatDate,
    getInitials(firstName, lastName) {
      if (!firstName && !lastName) return '?';
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    },
    getStatusClass(status) {
      const statusMap = {
        'new': 'status-new',
        'contacted': 'status-contacted',
        'interview': 'status-interview',
        'hired': 'status-hired',
        'rejected': 'status-rejected'
      };
      return statusMap[status] || 'status-new';
    },
    getStatusLabel(status) {
      const statusMap = {
        'new': 'Neu',
        'contacted': 'Kontaktiert',
        'interview': 'Interview',
        'hired': 'Eingestellt',
        'rejected': 'Abgelehnt'
      };
      return statusMap[status] || 'Neu';
    },
    async loadApplicants() {
      this.loading = true;
      this.error = null;
      
      try {
        console.log('Lade Bewerber...');
        if (!this.applicantsStore) {
          console.error('Der Store ist nicht initialisiert!');
          this.error = 'Interner Fehler: Store nicht verfügbar';
          return;
        }
        
        // Bewerber vom Backend laden
        const data = await this.applicantsStore.fetchApplicants();
        console.log('Bewerberdaten geladen:', data);
        
        // Aktualisiere lokale Kopie als Fallback
        this.localApplicants = Array.isArray(data) ? [...data] : [];
      } catch (error) {
        console.error('Fehler beim Laden der Bewerber:', error);
        this.error = error.message || 'Bewerber konnten nicht geladen werden. Bitte versuchen Sie es später erneut.';
      } finally {
        this.loading = false;
      }
    },
    sortBy(key) {
      if (this.filters.sort === key) {
        this.filters.direction = this.filters.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sort = key;
        this.filters.direction = 'asc';
      }
    },
    viewApplicant(applicant) {
      this.$router.push({ 
        name: 'applicant-detail',
        params: { id: applicant.id } 
      });
    },
    async deleteApplicant(applicant) {
      if (!confirm(`Sind Sie sicher, dass Sie den Bewerber ${applicant.first_name} ${applicant.last_name} löschen möchten?`)) {
        return;
      }
      
      this.loading = true;
      
      try {
        await this.applicantsStore.deleteApplicant(applicant.id);
        this.$toast.success('Bewerber erfolgreich gelöscht');
        // Aktualisiere die Bewerberliste
        await this.loadApplicants();
      } catch (error) {
        this.$toast.error('Fehler beim Löschen des Bewerbers');
        console.error('Fehler beim Löschen des Bewerbers:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // Erstellt einen Test-Bewerber für Demonstrationszwecke
    async createTestApplicant() {
      this.loading = true;
      
      try {
        // Zufällige Daten für einen Testbewerber
        const randomId = Math.floor(Math.random() * 10000);
        const testApplicant = {
          first_name: 'Test',
          last_name: `Bewerber ${randomId}`,
          email: `test${randomId}@example.com`,
          phone: `+49 123 45678${randomId.toString().substring(0, 2)}`,
          status: 'new',
          // Explizit die company_id des angemeldeten Benutzers verwenden
          company_id: this.user?.company_id || 1
        };
        
        console.log('Erstelle Testbewerber mit Daten:', testApplicant);
        
        // Bewerber erstellen
        const response = await this.applicantsStore.createApplicant(testApplicant);
        console.log('Bewerber erstellt, Antwort:', response);
        
        // Erfolgsmeldung anzeigen
        this.$toast.success('Testbewerber wurde erfolgreich erstellt');
        
        // Bewerberliste aktualisieren - mit kurzem Delay, um dem Backend Zeit zu geben
        setTimeout(async () => {
          try {
            await this.loadApplicants();
          } catch (loadError) {
            console.error('Fehler beim Neuladen der Bewerberliste:', loadError);
          }
        }, 500);
      } catch (error) {
        console.error('Fehler beim Erstellen des Testbewerbers:', error);
        
        // Detaillierte Fehlerinformationen anzeigen
        if (error.response) {
          console.error('API-Fehlerdetails:', {
            status: error.response.status,
            data: error.response.data
          });
          
          if (error.response.data && error.response.data.message) {
            this.$toast.error(`Fehler: ${error.response.data.message}`);
          } else {
            this.$toast.error(`Fehler beim Erstellen des Testbewerbers: ${error.response.status}`);
          }
        } else {
          this.$toast.error('Fehler beim Erstellen des Testbewerbers');
        }
        
        // Wenn es ein Authentifizierungsfehler ist, zur Login-Seite weiterleiten
        if (error.response && error.response.status === 401) {
          this.$toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
          this.$router.push('/login');
        }
      } finally {
        this.loading = false;
      }
    },
    async userInfoRequest() {
      try {
        this.loading = true;
        const response = await this.authStore.fetchCurrentUser();
        console.log('Aktuelle Benutzerinformationen:', response);
        this.$toast.success('Benutzerinformationen erfolgreich aktualisiert');
      } catch (error) {
        console.error('Fehler beim Abrufen der Benutzerinformationen:', error);
        this.$toast.error('Fehler beim Abrufen der Benutzerinformationen');
      } finally {
        this.loading = false;
      }
    },
    
    async assignCompanyToUser() {
      try {
        this.loading = true;
        
        // Da wir möglicherweise keine Backend-API für die Zuweisung haben,
        // machen wir einen Workaround für Testzwecke
        
        // Zeige einen Info-Dialog an
        this.$toast.info('Für die Zuordnung einer Firma müsste der Administrator im Backend die company_id setzen.');
        
        // Benutzerinformationen aktualisieren
        await this.authStore.fetchCurrentUser();
        
        // Eine alternative Möglichkeit wäre, einen Testbewerber direkt zu erstellen
        this.$toast.info('Alternativ können Sie direkt einen Testbewerber erstellen. Die Firma wird bei der Erstellung vom Backend gesetzt.');
        
        console.log('Bitte fügen Sie im Backend dem Benutzer eine company_id hinzu oder erstellen Sie direkt einen Bewerber.');
        
      } catch (error) {
        console.error('Fehler bei der Benutzerinformationsaktualisierung:', error);
        this.$toast.error('Fehler bei der Benutzerinformationsaktualisierung');
      } finally {
        this.loading = false;
      }
    },
    // Debounce search input
    debounceSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        // Update the actual filter property after delay
        this.filters.search = this.searchQueryInput;
      }, 300); // 300ms delay
    },
    // Clear search input and filter
    clearSearch() {
      this.searchQueryInput = '';
      this.filters.search = '';
      clearTimeout(this.debounceTimeout);
    }
  }
};
</script>

<style lang="scss" scoped>
.applicants-container {
  --card-bg: #fff;
  --card-border: rgba(0, 0, 0, 0.1);
  --text-primary: #2c3e50;
  --text-secondary: #64748b;
  --hover-bg: #f8fafc;
  --table-header-bg: #f8fafc;
  --table-border: #e2e8f0;
  --status-bg: #f1f5f9;
  --avatar-bg: #e2e8f0;
  --icon-hover-bg: #f1f5f9;
  --search-border: #e2e8f0;
  --empty-state-color: #94a3b8;
  
  .dark-mode & {
    --card-bg: #1e293b;
    --card-border: rgba(255, 255, 255, 0.1);
    --text-primary: #e2e8f0;
    --text-secondary: #94a3b8;
    --hover-bg: #2c3e50;
    --table-header-bg: #1e293b;
    --table-border: #334155;
    --status-bg: #334155;
    --avatar-bg: #334155;
    --icon-hover-bg: #334155;
    --search-border: #334155;
    --empty-state-color: #64748b;
  }

  padding: 2rem;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .page-title {
      font-size: 1.75rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
    }
    
    .actions {
      display: flex;
      gap: 1rem;
      align-items: center;
    }
  }

  .search-input {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--search-border);
    background: var(--card-bg);
    color: var(--text-primary);
    min-width: 300px;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
    }
    
    &::placeholder {
      color: var(--text-secondary);
    }
  }

  .content-container {
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border: 1px solid var(--card-border);
  }

  .applicants-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    
    th {
      background: var(--table-header-bg);
      color: var(--text-secondary);
      font-weight: 600;
      padding: 1rem;
      text-align: left;
      border-bottom: 2px solid var(--table-border);
      white-space: nowrap;
      
      &.sortable {
        cursor: pointer;
        transition: color 0.2s ease;
        
        &:hover {
          color: var(--primary-color);
        }
        
        i {
          margin-left: 0.5rem;
          font-size: 0.875rem;
        }
      }
    }
    
    td {
      padding: 1rem;
      color: var(--text-primary);
      border-bottom: 1px solid var(--table-border);
      transition: background-color 0.2s ease;
    }
    
    .applicant-row {
      &:hover td {
        background-color: var(--hover-bg);
      }
    }
  }

  .applicant-name {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: var(--avatar-bg);
      color: var(--text-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.875rem;
    }
    
    .name-details {
      .full-name {
        font-weight: 500;
        color: var(--text-primary);
      }
    }
  }

  .status-badge {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    background: var(--status-bg);
    
    &.status-active { color: #10b981; }
    &.status-pending { color: #f59e0b; }
    &.status-rejected { color: #ef4444; }
    &.status-hired { color: #3b82f6; }
  }

  .actions-cell {
    white-space: nowrap;
    
    .btn-icon {
      width: 36px;
      height: 36px;
      padding: 0;
      border: none;
      background: transparent;
      border-radius: 6px;
      color: var(--text-secondary);
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-left: 0.5rem;
      
      &:hover {
        background: var(--icon-hover-bg);
        color: var(--primary-color);
        transform: translateY(-2px);
      }
      
      &:first-child {
        margin-left: 0;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--empty-state-color);
    
    .empty-icon {
      font-size: 3rem;
      margin-bottom: 1.5rem;
      opacity: 0.5;
    }
    
    h3 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 1rem;
      color: var(--text-primary);
    }
    
    p {
      color: var(--text-secondary);
      margin-bottom: 1.5rem;
    }
  }

  .loading-container {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--text-secondary);
    
    .loader {
      width: 40px;
      height: 40px;
      border: 3px solid var(--primary-color);
      border-radius: 50%;
      border-top-color: transparent;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
}

/* Enhanced Header & Search Styles */
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap; // Allow wrapping on smaller screens
  gap: 1rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem; 
  .display-heading {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.75rem;
    color: var(--text-primary);
  }
}

.search-section {
  flex-grow: 1; 
  max-width: 400px; 
  min-width: 250px; 
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 1rem;
    color: var(--text-secondary);
    pointer-events: none; 
  }

  .search-input-enhanced {
    padding: 0.75rem 2.75rem 0.75rem 2.75rem; // Padding L/R for icons
    border-radius: 8px;
    border: 1px solid var(--search-border);
    background-color: var(--card-bg);
    color: var(--text-primary);
    width: 100%;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
      background-color: var(--card-bg); 
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }

  .clear-search-btn {
    position: absolute;
    right: 0.5rem;
    padding: 0.5rem;
    border: none;
    background: none;
    color: var(--text-secondary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: color 0.2s ease, background-color 0.2s ease;

    &:hover {
      color: var(--text-primary);
      background-color: var(--icon-hover-bg);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
    }

     i {
       font-size: 0.9rem;
     }
  }
}

/* Remove potentially conflicting old styles */
.search-section .input-group { 
  /* Remove styles if this structure was used before */
  border: none;
  padding: 0;
}
.search-section .input-group .form-control {
  /* Reset potentially conflicting styles */
}

/* Ensure card padding doesn't conflict */
.card-with-table {
  // Adjust padding if necessary, e.g., padding: 0; and add padding to inner divs
}
</style>